/**
 * Modifier groups - the questions the till asks after an item is tapped.
 *
 * A group is authored once ("Spice level", "Add extras") and attached to as
 * many items as need it, which is why they live on their own screen rather than
 * inside the item editor.
 *
 * Editing the groups themselves is a database job for now; this screen shows
 * what exists and which items use it, and lets you attach a group to an item -
 * which is the part that changes week to week.
 */

import { useState, useCallback } from 'react';
import { FiSliders, FiSearch, FiCheck } from 'react-icons/fi';

import { menuApi } from '../services/endpoints';
import useAsync, { useAction } from '../hooks/useAsync';
import Modal from '../components/ui/Modal';
import { PageHeader, Badge, EmptyState } from '../components/ui/Feedback';
import { SpinnerBlock } from '../components/ui/Spinner';
import { Checkbox } from '../components/ui/Field';
import { money } from '../utils/format';

const ModifiersPage = () => {
    const [attachTo, setAttachTo] = useState(null);
    const [search, setSearch] = useState('');

    const loadGroups = useCallback(() => menuApi.listModifierGroups(), []);
    const { data: groups, loading } = useAsync(loadGroups, []);

    const loadItems = useCallback(() => menuApi.listItems({ search }), [search]);
    const { data: items, refetch: refetchItems } = useAsync(loadItems, [search]);

    if (loading) return <SpinnerBlock label="Loading modifiers…" />;

    return (
        <div className="page-scroll">
            <PageHeader
                title="Modifiers"
                subtitle="Extras, sizes of choice, and the questions asked at the till"
            />

            {!groups?.length ? (
                <EmptyState
                    className="mt-6"
                    icon={FiSliders}
                    title="No modifier groups"
                    description="Add them to MODIFIER_GROUP and MODIFIER, or run the demo seed script."
                />
            ) : (
                <div className="mt-2 grid gap-2 sm:gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {groups.map((group) => (
                        <section key={group.Modifier_Group_ID} className="card p-2">
                            <header className="mb-3 flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <h2 className="truncate font-semibold text-slate-800">
                                        {group.Group_Name}
                                    </h2>
                                    <p className="text-xs text-slate-500">
                                        {group.Is_Required ? 'Required · ' : 'Optional · '}
                                        {group.Max_Selection === 1
                                            ? 'choose one'
                                            : `choose up to ${group.Max_Selection}`}
                                    </p>
                                </div>
                                {group.Is_Required && <Badge tone="amber">required</Badge>}
                            </header>

                            <ul className="space-y-1.5">
                                {group.modifiers.map((modifier) => (
                                    <li
                                        key={modifier.Modifier_ID}
                                        className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-1 text-sm"
                                    >
                                        <span className="min-w-0 truncate text-slate-700">
                                            {modifier.Modifier_Name}
                                            {modifier.Is_Default_Selected && (
                                                <Badge tone="blue" className="ml-2">default</Badge>
                                            )}
                                        </span>
                                        <span className="tabular shrink-0 font-medium text-slate-800">
                                            {Number(modifier.Additional_Price) > 0
                                                ? `+ ${money(modifier.Additional_Price)}`
                                                : 'free'}
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            <button
                                type="button"
                                className="btn-secondary btn-sm mt-1 w-full"
                                onClick={() => setAttachTo(group)}
                            >
                                Attach to items
                            </button>
                        </section>
                    ))}
                </div>
            )}

            {/* Attaching a group to items */}
            <AttachModal
                group={attachTo}
                items={items || []}
                search={search}
                onSearch={setSearch}
                onClose={() => setAttachTo(null)}
                onSaved={refetchItems}
            />
        </div>
    );
};

const AttachModal = ({ group, items, search, onSearch, onClose, onSaved }) => {
    const [selected, setSelected] = useState(new Set());

    const { run: attach, busy } = useAction(async () => {
        // The endpoint sets the full list per item, so each selected item has
        // this group added to whatever it already had.
        for (const menuItemId of selected) {
            const detail = await menuApi.getItem(menuItemId);
            const existing = new Set(
                (detail.item.modifierGroups || []).map((g) => g.Modifier_Group_ID)
            );
            existing.add(group.Modifier_Group_ID);
            await menuApi.setModifierGroups(menuItemId, [...existing]);
        }
    }, {
        successMessage: 'Attached.',
        onSuccess: () => { setSelected(new Set()); onSaved(); onClose(); }
    });

    const toggle = (menuItemId) => {
        setSelected((current) => {
            const next = new Set(current);
            if (next.has(menuItemId)) next.delete(menuItemId);
            else next.add(menuItemId);
            return next;
        });
    };

    return (
        <Modal
            open={!!group}
            onClose={onClose}
            title={`Attach "${group?.Group_Name || ''}"`}
            subtitle="The till will ask this question when any of these items is tapped."
            width="md"
            dismissible={!busy}
            footer={(
                <>
                    <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={() => attach()}
                        disabled={busy || selected.size === 0}
                    >
                        <FiCheck /> {busy ? 'Attaching…' : `Attach to ${selected.size} item(s)`}
                    </button>
                </>
            )}
        >
            <div className="relative mb-3">
                <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                    className="input pl-9"
                    placeholder="Search menu items"
                    value={search}
                    onChange={(event) => onSearch(event.target.value)}
                    data-autofocus
                />
            </div>

            <div className="max-h-80 space-y-1 overflow-y-auto">
                {items.map((item) => (
                    <Checkbox
                        key={item.Menu_Item_ID}
                        label={item.Item_Name}
                        description={item.Category_Name}
                        checked={selected.has(item.Menu_Item_ID)}
                        onChange={() => toggle(item.Menu_Item_ID)}
                        className="rounded-lg px-2 py-1.5 hover:bg-slate-50"
                    />
                ))}
            </div>
        </Modal>
    );
};

export default ModifiersPage;
